import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { FIT_FACT_ITEMS } from '../constants/facts';
import { useLocale } from '../context/LocaleContext';

const QuestGenerationLoading = ({ isGenerating, isFinished, onComplete, error, triggerSource, startedAt, title, subtitle }) => {
    const { t } = useLocale();
    const [progress, setProgress] = useState(0);
    const [currentFactIndex, setCurrentFactIndex] = useState(0);
    const [showFact, setShowFact] = useState(true);

    // Simulate progress bar
    useEffect(() => {
        let interval;

        if (isFinished) {
            // Fast forward to 100% when finished
            interval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        if (onComplete) setTimeout(onComplete, 500); // Wait 500ms at 100% before triggering completion
                        return 100;
                    }
                    return Math.min(prev + 2, 100); // Fast increment
                });
            }, 20);
        } else if (isGenerating) {
            // Slow progress logic (Target ~90s)
            interval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) return prev;

                    // Non-linear progress to feel responsive but last 90s
                    let increment = 0.05;
                    if (prev < 15) increment = 0.5; // Fast start (0-15%)
                    else if (prev < 50) increment = 0.1; // Steady (15-50%)
                    else increment = 0.03; // Slow crawl (50-90%) - Main "thinking" time

                    return Math.min(prev + increment, 90);
                });
            }, 100);
        } else {
            setProgress(0);
        }

        return () => clearInterval(interval);
    }, [isGenerating, isFinished, onComplete]);

    // Cycle facts
    useEffect(() => {
        if (!isGenerating) return;

        // Pick a random start index
        setCurrentFactIndex(Math.floor(Math.random() * FIT_FACT_ITEMS.length));

        const interval = setInterval(() => {
            setShowFact(false);
            setTimeout(() => {
                setCurrentFactIndex((prev) => (prev + 1) % FIT_FACT_ITEMS.length);
                setShowFact(true);
            }, 500); // Wait for fade out
        }, 6000); // Change every 6 seconds

        return () => clearInterval(interval);
    }, [isGenerating]);

    if (error) {
        return (
            <div className="bg-red-900/60 rounded-2xl p-6 text-left text-red-200 border border-red-500/30 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-bold text-red-100 text-lg">{t('questGenerationLoading.errorTitle', 'Quest Generation Failed')}</p>
                </div>
                <p className="text-sm text-red-200/80 ml-9">{error}</p>
                <p className="text-xs text-red-300/60 ml-9 mt-4">
                    {t('questGenerationLoading.errorBody', 'Please try again later or contact support.')}
                </p>
            </div>
        );
    }

    const currentFact = FIT_FACT_ITEMS[currentFactIndex] || FIT_FACT_ITEMS[0];

    return (
        <div className="relative overflow-hidden bg-[#2C2F33]/80 backdrop-blur-md rounded-3xl p-8 text-center border border-white/5 shadow-2xl">
            {/* Background Ambient Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-cyan-500/5 to-transparent pointer-events-none" />

            {/* Main Animation Container */}
            <div className="relative flex justify-center items-center mb-8 mt-4">
                {/* Pulsing Rings */}
                <div className="absolute w-32 h-32 bg-cyan-500/20 rounded-full animate-ping opacity-20" />
                <div className="absolute w-24 h-24 bg-cyan-400/10 rounded-full animate-pulse" />

                {/* Center Icon/Logo */}
                <div className="relative z-10 bg-gradient-to-br from-[#2C2F33] to-[#1a1d21] p-4 rounded-full border border-cyan-500/30 shadow-[0_0_30px_rgba(34,211,238,0.2)]">
                    <svg className="w-10 h-10 text-cyan-400 animate-spin-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </div>
            </div>

            {/* Text Content */}
            <div className="relative z-10 space-y-2 mb-8">
                <h3 className="text-xl font-bold text-white tracking-tight">
                    {title || t('questGenerationLoading.title', 'Building Your Plan...')}
                </h3>
                <p className="text-sm text-cyan-200/60 font-medium">
                    {subtitle || t('questGenerationLoading.subtitle', 'Reviewing your preferences & goals')}
                </p>
            </div>

            {/* Progress Bar */}
            <div className="relative z-10 max-w-xs mx-auto mb-8">
                <div className="h-1.5 w-full bg-gray-700/50 rounded-full overflow-hidden backdrop-blur-sm">
                    <div
                        className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(6,182,212,0.5)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="flex justify-between mt-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    <span>{t('questGenerationLoading.initiating', 'Initiating')}</span>
                    <span className="text-cyan-400">{Math.round(progress)}%</span>
                    <span>{t('questGenerationLoading.finalizing', 'Finalizing')}</span>
                </div>
            </div>

            {/* Dynamic Fact Card */}
            <div className="relative z-10 bg-gradient-to-br from-gray-800/40 to-gray-900/40 border border-white/5 rounded-xl p-5 max-w-sm mx-auto backdrop-blur-md transition-all duration-500">
                <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                    <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest">
                        {t('questGenerationLoading.didYouKnow', 'Did You Know?')}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                </div>
                <div className={`transition-opacity duration-500 ${showFact ? 'opacity-100' : 'opacity-0'}`}>
                    <p className="text-gray-300 text-sm italic leading-relaxed font-medium">
                        "{currentFact?.text || ''}"
                    </p>
                    {currentFact ? (
                        <div className="mt-3">
                            <Link
                                to={`/health-sources?fact=${encodeURIComponent(currentFact.id)}`}
                                className="text-[11px] font-semibold text-cyan-300 hover:text-cyan-200"
                            >
                                {t('questGenerationLoading.seeSources', 'See sources')}
                            </Link>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Footer Info */}
            {(triggerSource || startedAt) && (
                <div className="mt-6 text-[10px] text-gray-600 font-medium uppercase tracking-widest">
                    {triggerSource && <span>{triggerSource}</span>}
                    {triggerSource && startedAt && <span className="mx-2">•</span>}
                    {startedAt && <span>{startedAt}</span>}
                </div>
            )}

            <style jsx>{`
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
      `}</style>
        </div>
    );
};

export default QuestGenerationLoading;
